import type { BlockType, ComponentsUnionType } from '@unifyapps/defs/types/block';
import type { DataSource, DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { InterfacePageMetadata } from '@unifyapps/defs/types/page';
import { createContext } from 'use-context-selector';
import type { StoreApi } from 'zustand';
import type { WithSelectors } from '../../../../utils/state/zustand';
import type { DependencyGraphContextType } from '../../../context/DependencyGraphProvider/types';
import type {
  GetComputeContextBuilder,
  UseNonReactiveComputeContextType,
  UseReactiveComputeContextType,
} from '../../../hooks/computeContext';
import type useBlockEvents from '../../../hooks/useBlockEvents';
import type { createGlobalAppStateStoreSelectors } from '../../../stores/app/GlobalAppStateStore/selectors';
import type { DataSourceRecordStoreType } from '../../../stores/DataSourceRecordStore';
import type { BlockLifecycleStatus } from '../../../stores/GlobalStateStore';
import type { createGlobalStateStoreSelectors } from '../../../stores/GlobalStateStore/selectors';
import type { InterfaceModes } from '../../../stores/InterfaceStore';

type DataSourceControllerContextType = {
  // these are values from InterfaceStore because we do not have the store instance in global provider yet, so we need to pass them as a props
  blocks?: Record<string, BlockType<ComponentsUnionType>>;
  dataSourcesFromPage?: Record<string, DataSource>;
  entityTypeDataSources?: Record<string, DataSourceEntity | undefined>;
  metadata?: InterfacePageMetadata;
  // this is used to determine if the page is public or not, so that any data source called from this page can switch from /api/ to /auth/ based on this value
  isPublicPage?: boolean;
  activePageSlug?: string;
  mode: InterfaceModes;
  interfaceRecord?: InterfaceEntity;

  // value from GlobalStateStore because it is not present in GlobalAppStateStore we are passing it as a prop
  blockLifecycles?: Record<string, BlockLifecycleStatus | undefined>;

  // Pulse manager id for this controller's store. Both controllers supply it: the page controller
  // from useGlobalStateStoreManager(), the app controller from the APP_MANAGER_ID constant. The
  // node combines it with usePageInstanceId() to get its true scope — see useDataSourcePulseScope.
  // Optional because useDataSourcePulseScope still degrades to no-scope/no-emit if it's ever absent.
  managerId?: string;

  getComputeContext: GetComputeContextBuilder;
  // hooks
  useNonReactiveComputeContext: UseNonReactiveComputeContextType;
  useReactiveComputeContext: UseReactiveComputeContextType;

  /**
   * because we wrap data source record store two times, one at app level and various at page level
   * so we are passing the instance of our hook to the controller context so that it can use the correct instance of the store
   */
  useDataSourceRecordStore: WithSelectors<StoreApi<DataSourceRecordStoreType>>;

  // the global state store of the app level and page level are of different types but here we are passing the union so that
  useGlobalStateStore:
    | ReturnType<typeof createGlobalStateStoreSelectors>
    | ReturnType<typeof createGlobalAppStateStoreSelectors>;

  useDependencyGraphProvider: DependencyGraphContextType;

  /**
   * in data source callbacks we need to handle events for that we need to getComputeContext which is different for app level and page level so we do this
   */
  useBlockEvents: typeof useBlockEvents;

  allowedDataSourceResourceNames: Set<string>;
};

export const DataSourceControllerContext = createContext<DataSourceControllerContextType | null>(
  null,
);

export type { DataSourceControllerContextType };
